import { useState, useEffect, useRef } from 'react';
import RetailerLayout from './RetailerLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { rshd, drlp, barcode, ocr, getErrorMessage } from '../../utils/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Package, Camera, Barcode, Upload, Loader2, Trash2, Plus, ShoppingCart, Store, Zap } from 'lucide-react';
import { getTier } from '../../utils/discountTiers';
import DealReachPreview from './DealReachPreview';

// DealShaq 20-Category Taxonomy (Top-level only)
// DealShaq 20-Category Taxonomy (sorted A-Z, Miscellaneous last)
const CATEGORIES = [
  "Baby & Kids", "Bakery & Bread", "Beverages", "Breakfast & Cereal",
  "Dairy & Eggs", "Deli & Prepared Foods", "Frozen Foods",
  "Fruits", "Health & Nutrition", "Household Essentials",
  "Meat & Poultry", "Oils, Sauces & Spices", "Pantry Staples",
  "Pasta, Rice & Grains", "Personal Care", "Pet Supplies",
  "Seafood", "Snacks & Candy", "Vegetables", "Miscellaneous"
];

export default function RetailerPostItem({ user, onLogout }) {
  const navigate = useNavigate();
  const [hasLocation, setHasLocation] = useState(false);
  const [batchItems, setBatchItems] = useState([]); // Items in the current batch
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    regular_price: '',
    discount_level: '1',
    quantity: '',
    barcode: '',
    weight: '',
    image_url: '',
    is_taxable: true,
    attributes: {},
  });
  const [scanning, setScanning] = useState(false);
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [barcodeScanProcessing, setBarcodeScanProcessing] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [postingBatch, setPostingBatch] = useState(false);
  const priceImageRef = useRef(null);
  const barcodeImageRef = useRef(null);

  // Canonical tier info (single source: utils/discountTiers)
  const getDiscountInfo = () => {
    const t = getTier(formData.discount_level);
    return { drlp: t.drlp, consumer: t.consumer, name: t.fullName };
  };

  const calculateDealPrice = () => {
    if (!formData.regular_price) return '0.00';
    const price = parseFloat(formData.regular_price);
    const discountInfo = getDiscountInfo();
    return (price * (1 - discountInfo.consumer / 100)).toFixed(2);
  };

  // Live economics for the breakdown panel (per-unit + totals)
  const getMargins = () => {
    const regular = parseFloat(formData.regular_price) || 0;
    const qty = parseInt(formData.quantity) || 0;
    const { drlp, consumer } = getDiscountInfo();
    const unit = {
      shopperPays: regular * (1 - consumer / 100),
      shopperSaves: regular * (consumer / 100),
      grocerReceives: regular * (1 - drlp / 100),
      dealshaqMargin: regular * ((drlp - consumer) / 100),
    };
    const totals = {
      shopperPays: unit.shopperPays * qty,
      shopperSaves: unit.shopperSaves * qty,
      grocerReceives: unit.grocerReceives * qty,
      dealshaqMargin: unit.dealshaqMargin * qty,
    };
    return { regular, qty, drlp, consumer, spreadPct: drlp - consumer, unit, totals };
  };

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data:image/xxx;base64, prefix
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  // Real barcode lookup using Open Food Facts API
  const handleBarcodeLookup = async () => {
    const barcodeToLookup = barcodeInput || formData.barcode;
    if (!barcodeToLookup) {
      toast.error('Please enter a barcode number');
      return;
    }
    
    setScanning(true);
    try {
      const response = await barcode.lookup(barcodeToLookup);
      
      if (response.data.success && response.data.product) {
        const product = response.data.product;
        setFormData({
          ...formData,
          name: product.name || formData.name,
          category: product.category || formData.category,
          barcode: barcodeToLookup,
          weight: product.weight?.toString() || formData.weight,
          description: product.description || formData.description,
          image_url: product.image_url || formData.image_url,
          attributes: {
            ...formData.attributes,
            organic: product.is_organic || false,
            brand: product.brand || '',
          }
        });
        toast.success(`Product found: ${product.name}`);
      } else {
        // Product not found - show friendly message and still set the barcode
        const friendlyError = response.data.error || "This barcode wasn't found in our database. Please enter the product details manually.";
        toast.info(friendlyError);
        setFormData({ ...formData, barcode: barcodeToLookup });
      }
    } catch (error) {
      // Network or unexpected error - show friendly message
      toast.error("Unable to look up barcode. Please enter the product details manually.");
      setFormData({ ...formData, barcode: barcodeToLookup });
    } finally {
      setScanning(false);
      setBarcodeInput('');
    }
  };

  // Barcode image scan - extract barcode from uploaded image
  const handleBarcodeImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WEBP image');
      return;
    }
    
    setBarcodeScanProcessing(true);
    try {
      const imageBase64 = await fileToBase64(file);
      const response = await barcode.scanImage(imageBase64);
      
      if (response.data.success) {
        const { barcode: extractedBarcode, product, product_found } = response.data;
        
        if (product_found && product) {
          // Product found in database - populate all fields
          setFormData({
            ...formData,
            name: product.name || formData.name,
            category: product.category || formData.category,
            barcode: extractedBarcode,
            weight: product.weight?.toString() || formData.weight,
            description: product.description || formData.description,
            image_url: product.image_url || formData.image_url,
            attributes: {
              ...formData.attributes,
              organic: product.is_organic || false,
              brand: product.brand || '',
            }
          });
          toast.success(`Product found: ${product.name}`);
        } else {
          // Barcode extracted but product not in database - still helpful!
          setFormData({ ...formData, barcode: extractedBarcode });
          toast.info(`Barcode ${extractedBarcode} scanned! Product not in database - please enter details manually.`);
        }
      } else {
        // Could not extract barcode from image
        const errorMsg = response.data.error || "Couldn't read the barcode from this image. Please try a clearer photo or type the barcode manually.";
        toast.warning(errorMsg);
      }
    } catch (error) {
      toast.error("Unable to scan barcode from image. Please try again or type the barcode manually.");
    } finally {
      setBarcodeScanProcessing(false);
      if (barcodeImageRef.current) {
        barcodeImageRef.current.value = '';
      }
    }
  };

  // OCR price extraction from image
  const handlePriceImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WEBP image');
      return;
    }
    
    setOcrProcessing(true);
    try {
      const imageBase64 = await fileToBase64(file);
      const response = await ocr.extractPrice(imageBase64);
      
      if (response.data.success && response.data.extracted) {
        const extracted = response.data.extracted;
        
        if (extracted.price) {
          // Clean the price (remove $ and other characters)
          const cleanPrice = extracted.price.replace(/[^0-9.]/g, '');
          setFormData({
            ...formData,
            regular_price: cleanPrice,
            name: extracted.product_name || formData.name,
          });
          toast.success(`Price extracted: $${cleanPrice}`);
        } else if (extracted.raw_text) {
          toast.info('Could not extract structured price. Please check the image.');
        }
      }
    } catch (error) {
      const errorMsg = getErrorMessage(error, 'Failed to extract price from image');
      toast.error(errorMsg);
    } finally {
      setOcrProcessing(false);
      // Reset file input
      if (priceImageRef.current) {
        priceImageRef.current.value = '';
      }
    }
  };

  // Legacy mock scan (kept for fallback/demo)
  const handleBarcodeScan = async () => {
    // This is now replaced by handleBarcodeLookup
    // Keeping for backwards compatibility with any existing UI elements
    handleBarcodeLookup();
  };

  const handlePriceScan = async () => {
    // This is now replaced by handlePriceImageUpload
    // Trigger file input click for price image upload
    if (priceImageRef.current) {
      priceImageRef.current.click();
    }
  };

  useEffect(() => {
    checkLocation();
  }, []);

  const checkLocation = async () => {
    try {
      await drlp.myLocation();
      setHasLocation(true);
    } catch (error) {
      setHasLocation(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!hasLocation) {
      toast.error('Please set up your location first');
      return;
    }

    // Validate minimum quantity
    if (parseInt(formData.quantity) < 5) {
      toast.error('Each sizzling hot deal item must have an initial quantity of 5 or more');
      return;
    }

    // Add item to batch instead of posting directly
    const discountInfo = getDiscountInfo();
    const newItem = {
      id: Date.now(), // Temporary ID for UI
      name: formData.name,
      description: formData.description || '',
      category: formData.category,
      subcategory: '',
      regular_price: parseFloat(formData.regular_price),
      discount_level: parseInt(formData.discount_level),
      quantity: parseInt(formData.quantity),
      barcode: formData.barcode || '',
      weight: formData.weight ? parseFloat(formData.weight) : null,
      image_url: formData.image_url || '',
      is_taxable: formData.is_taxable,
      attributes: formData.attributes || {},
      // Calculated fields for display
      deal_price: calculateDealPrice(),
      consumer_discount: discountInfo.consumer,
      deal_name: discountInfo.name,
    };

    setBatchItems([...batchItems, newItem]);
    toast.success(`"${formData.name}" added to batch (${batchItems.length + 1} items)`);
    
    // Reset form for next item
    setFormData({
      name: '',
      description: '',
      category: '',
      regular_price: '',
      discount_level: '1',
      quantity: '',
      barcode: '',
      weight: '',
      image_url: '',
      is_taxable: true,
      attributes: {},
    });
    setBarcodeInput('');
    
    // Scroll to top so associate can easily add next item
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Remove item from batch
  const removeFromBatch = (itemId) => {
    setBatchItems(batchItems.filter(item => item.id !== itemId));
    toast.info('Item removed from batch');
  };

  // Post all items in batch
  const handlePostBatch = async () => {
    if (batchItems.length < 2) {
      toast.error('Please add at least 2 items to the batch before posting');
      return;
    }

    setPostingBatch(true);

    try {
      // Prepare batch payload - only include fields needed by the backend
      const batchPayload = batchItems.map(item => ({
        name: item.name,
        description: item.description,
        category: item.category,
        subcategory: item.subcategory || '',
        regular_price: item.regular_price,
        discount_level: item.discount_level,
        quantity: item.quantity,
        barcode: item.barcode || '',
        weight: item.weight,
        image_url: item.image_url || '',
        is_taxable: item.is_taxable,
        attributes: item.attributes || {},
      }));

      const response = await rshd.createBatch(batchPayload);
      const result = response.data;

      if (result.success) {
        toast.success(`All ${result.created_count} items posted successfully! DACs with matching favorites will be notified.`);
        setBatchItems([]);
        navigate('/retailer/inventory');
      } else if (result.created_count > 0) {
        toast.warning(`${result.created_count} items posted, ${result.failed_count} failed. Check inventory for details.`);
        // Remove successfully created items from batch, keep failed ones
        const failedIndices = new Set(result.failed_items.map(f => f.index));
        setBatchItems(batchItems.filter((_, idx) => failedIndices.has(idx)));
        if (result.created_count > 0) {
          navigate('/retailer/inventory');
        }
      } else {
        toast.error('Failed to post batch. Please check item details and try again.');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to post batch'));
    } finally {
      setPostingBatch(false);
    }
  };

  if (!hasLocation) {
    return (
      <RetailerLayout user={user} onLogout={onLogout}>
        <Card>
          <CardHeader>
            <CardTitle>Setup Required</CardTitle>
            <CardDescription>Please set up your store location first</CardDescription>
          </CardHeader>
          <CardContent>
            <SetupLocationForm onComplete={() => setHasLocation(true)} />
          </CardContent>
        </Card>
      </RetailerLayout>
    );
  }

  return (
    <RetailerLayout user={user} onLogout={onLogout}>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Post RSHD Items
          </h1>
          <p className="text-gray-600 mt-1">Post a Batch of 2 or more deals. Each item must have quantity ≥ 5. Items will be matched to interested DACs.</p>
        </div>

        {/* Batch Summary Card */}
        <Card className={`border-2 ${batchItems.length >= 2 ? 'border-green-500 bg-green-50' : 'border-yellow-500 bg-yellow-50'}`}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <ShoppingCart className="w-5 h-5" />
                <span>Current Batch</span>
                <Badge variant={batchItems.length >= 2 ? "default" : "secondary"} className={batchItems.length >= 2 ? "bg-green-600" : "bg-yellow-600"}>
                  {batchItems.length} / 2 minimum
                </Badge>
              </div>
              {batchItems.length >= 2 && (
                <Button
                  onClick={handlePostBatch}
                  disabled={postingBatch}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {postingBatch ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Posting...
                    </>
                  ) : (
                    <>
                      <Package className="w-4 h-4 mr-2" />
                      Post Batch ({batchItems.length} items)
                    </>
                  )}
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {batchItems.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No items in batch yet. Add items using the form below.</p>
            ) : (
              <div className="space-y-2">
                {batchItems.map((item, index) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-sm text-gray-500">
                          {item.category} • Qty: {item.quantity} • ${item.deal_price} ({item.consumer_discount}% off)
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge className={
                        item.discount_level === 3 ? 'bg-red-500' :
                        item.discount_level === 2 ? 'bg-yellow-500' : 'bg-green-500'
                      }>
                        {item.discount_level === 3 ? 'Sizzling Hot' :
                         item.discount_level === 2 ? 'Hot' : 'Sizzling'}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFromBatch(item.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {batchItems.length < 2 && (
                  <p className="text-yellow-700 text-sm text-center mt-2">
                    ⚠️ Add at least {2 - batchItems.length} more item{2 - batchItems.length > 1 ? 's' : ''} to post the batch
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit}>
          {/* Step 1: Barcode Capture */}
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Barcode className="w-5 h-5" />
                <span>Step 1: Product Identification</span>
              </CardTitle>
              <CardDescription>Scan barcode or upload product image to auto-populate details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Barcode Lookup */}
              <div className="space-y-2">
                <Label>Barcode Lookup (Open Food Facts)</Label>
                <div className="flex space-x-2">
                  <Input
                    placeholder="Enter barcode number (e.g., 3017620422003)"
                    value={barcodeInput}
                    onChange={(e) => setBarcodeInput(e.target.value)}
                    className="flex-1"
                    data-testid="barcode-input"
                  />
                  <Button
                    type="button"
                    onClick={handleBarcodeLookup}
                    disabled={scanning}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="lookup-barcode-btn"
                  >
                    {scanning ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Barcode className="w-4 h-4 mr-2" />
                        Lookup
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Barcode Image Scan */}
              <div className="space-y-2">
                <Label>Or Upload Barcode Image (AI Scan)</Label>
                <div className="flex space-x-2">
                  <input
                    type="file"
                    ref={barcodeImageRef}
                    onChange={handleBarcodeImageUpload}
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => barcodeImageRef.current?.click()}
                    disabled={barcodeScanProcessing}
                    className="flex-1"
                  >
                    {barcodeScanProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Scanning Barcode...
                      </>
                    ) : (
                      <>
                        <Camera className="w-4 h-4 mr-2" />
                        Scan Barcode from Photo
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-gray-500">Upload a photo of a barcode/UPC to scan and look up the product</p>
              </div>

              {/* Success indicator with clear button */}
              {formData.name && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-green-800">✓ Product Found</p>
                      <p className="text-sm text-green-700 mt-1">{formData.name}</p>
                      {formData.category && <p className="text-xs text-green-600 mt-1">Category: {formData.category}</p>}
                      {formData.barcode && <p className="text-xs text-green-600">Barcode: {formData.barcode}</p>}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          name: '',
                          description: '',
                          category: '',
                          barcode: '',
                          weight: '',
                          image_url: '',
                          attributes: {},
                        });
                        setBarcodeInput('');
                        toast.info('Product cleared - you can start over');
                      }}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Step 2: Price & Details */}
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Camera className="w-5 h-5" />
                <span>Step 2: Capture Price</span>
              </CardTitle>
              <CardDescription>Upload price tag image (OCR) or enter manually</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Hidden file input for price image */}
              <input
                type="file"
                ref={priceImageRef}
                onChange={handlePriceImageUpload}
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
              />
              
              <div className="flex space-x-2 items-center">
                <Button
                  type="button"
                  onClick={() => priceImageRef.current?.click()}
                  disabled={ocrProcessing}
                  variant="outline"
                  className="flex-1"
                  data-testid="scan-price-btn"
                >
                  {ocrProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing OCR...
                    </>
                  ) : (
                    <>
                      <Camera className="w-4 h-4 mr-2" />
                      Upload Price Tag Photo
                    </>
                  )}
                </Button>
                <span className="text-gray-500 py-2">or</span>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="$0.00"
                  value={formData.regular_price}
                  onChange={(e) => setFormData({ ...formData, regular_price: e.target.value })}
                  className="flex-1"
                  data-testid="manual-price"
                  required
                />
              </div>
            </CardContent>
          </Card>

          {/* Step 3: Deal Configuration */}
          <Card className="mb-4">
            <CardHeader>
              <CardTitle>Step 3: Configure Deal</CardTitle>
              <CardDescription>Set discount level and quantity</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="name">Item Name * {formData.name && <span className="text-green-600 text-xs">(Auto-populated)</span>}</Label>
                  <Input
                    id="name"
                    data-testid="item-name"
                    placeholder="Will be auto-filled by barcode scan"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    data-testid="item-description"
                    placeholder="5 lb bag of fresh organic apples"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="category">Category * (Top-level only)</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                    <SelectTrigger data-testid="item-category">
                      <SelectValue placeholder="Select from 20 categories" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">20-category taxonomy for efficient matching</p>
                </div>

                <div>
                  <Label htmlFor="regular_price">Regular Price *</Label>
                  <Input
                    id="regular_price"
                    data-testid="item-regular-price"
                    type="number"
                    step="0.01"
                    placeholder="9.99"
                    value={formData.regular_price}
                    onChange={(e) => setFormData({ ...formData, regular_price: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="discount_level">Discount Level *</Label>
                  <Select value={formData.discount_level} onValueChange={(value) => setFormData({ ...formData, discount_level: value })}>
                    <SelectTrigger data-testid="item-discount-level">
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Sizzling - You: 60% → Consumer: 50%</SelectItem>
                      <SelectItem value="2">Hot - You: 75% → Consumer: 60%</SelectItem>
                      <SelectItem value="3">Sizzling Hot - You: 90% → Consumer: 75%</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    You discount to DealShaq, consumer sees mapped discount
                  </p>
                </div>

                <div className="md:col-span-2 p-4 bg-gradient-to-br from-slate-50 to-blue-50 rounded-lg border border-blue-200" data-testid="margin-breakdown">
                  {(() => {
                    const m = getMargins();
                    const fmt = (n) => `$${(n || 0).toFixed(2)}`;
                    const hasQty = m.qty > 0;
                    const amt = (unitVal, totalVal) => hasQty ? `${fmt(totalVal)} (${fmt(unitVal)})` : fmt(unitVal);
                    return (
                      <>
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-semibold text-gray-900">Live Margin Breakdown</h4>
                          <Badge variant="outline" className="text-xs" data-testid="margin-tier-name">{getDiscountInfo().name}</Badge>
                        </div>
                        <p className="text-xs text-gray-500 mb-3">
                          {hasQty ? `Totals for ${m.qty} unit${m.qty > 1 ? 's' : ''} · per-unit in parentheses` : 'Per unit — add a quantity to see totals'}
                        </p>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between" data-testid="margin-shopper-pays">
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 text-emerald-700"><ShoppingCart className="w-4 h-4" /></span>
                              <div>
                                <p className="text-sm font-medium text-gray-900">Shopper pays</p>
                                <p className="text-xs text-gray-500">{m.consumer}% off · saves {fmt(hasQty ? m.totals.shopperSaves : m.unit.shopperSaves)}</p>
                              </div>
                            </div>
                            <p className="text-base font-bold text-emerald-700 tabular-nums">{amt(m.unit.shopperPays, m.totals.shopperPays)}</p>
                          </div>
                          <div className="flex items-center justify-between" data-testid="margin-you-receive">
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700"><Store className="w-4 h-4" /></span>
                              <div>
                                <p className="text-sm font-medium text-gray-900">You receive</p>
                                <p className="text-xs text-gray-500">{100 - m.drlp}% of regular · {m.drlp}% off to DealShaq</p>
                              </div>
                            </div>
                            <p className="text-base font-bold text-blue-700 tabular-nums">{amt(m.unit.grocerReceives, m.totals.grocerReceives)}</p>
                          </div>
                          <div className="flex items-center justify-between" data-testid="margin-dealshaq">
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 text-amber-700"><Zap className="w-4 h-4" /></span>
                              <div>
                                <p className="text-sm font-medium text-gray-900">DealShaq margin</p>
                                <p className="text-xs text-gray-500">{m.spreadPct}% spread · funds ops &amp; charity</p>
                              </div>
                            </div>
                            <p className="text-base font-bold text-amber-700 tabular-nums">{amt(m.unit.dealshaqMargin, m.totals.dealshaqMargin)}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-3 pt-2 border-t border-blue-200 text-xs text-gray-500">
                          <span>Regular {fmt(m.regular)}{hasQty ? ` × ${m.qty}` : ' / unit'}</span>
                          <span className="font-medium text-gray-700">Deal price {fmt(m.unit.shopperPays)}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div>
                  <Label htmlFor="quantity">Quantity Available * (min. 5)</Label>
                  <Input
                    id="quantity"
                    data-testid="item-quantity"
                    type="number"
                    min="5"
                    placeholder="5"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum quantity of 5 required per RSHD item</p>
                </div>

                <div>
                  <Label htmlFor="barcode">Barcode (UPC/PLU)</Label>
                  <div className="flex space-x-2">
                    <Input
                      id="barcode"
                      data-testid="item-barcode"
                      placeholder="012345678901"
                      value={formData.barcode}
                      onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                    />
                    <Button type="button" variant="outline" size="icon">
                      <Barcode className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="weight">Weight (lbs, optional)</Label>
                  <Input
                    id="weight"
                    data-testid="item-weight"
                    type="number"
                    step="0.01"
                    placeholder="5.0"
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="image_url">Image URL</Label>
                  <div className="flex space-x-2">
                    <Input
                      id="image_url"
                      data-testid="item-image-url"
                      placeholder="https://..."
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    />
                    <Button type="button" variant="outline" size="icon">
                      <Camera className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <DealReachPreview />

              <Button
                data-testid="post-item-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                {loading ? 'Adding...' : 'Add to Batch'}
              </Button>
            </CardContent>
          </Card>
        </form>
      </div>
    </RetailerLayout>
  );
}

function SetupLocationForm({ onComplete }) {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    charity_id: '',
  });
  const [charities, setCharities] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCharities();
  }, []);

  const loadCharities = async () => {
    try {
      const { charities: charitiesApi } = await import('../../utils/api');
      const response = await charitiesApi.list();
      setCharities(response.data);
    } catch (error) {
      console.error('Failed to load charities:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await drlp.createLocation({
        ...formData,
        coordinates: { lat: 0, lng: 0 },
        operating_hours: '9 AM - 9 PM',
      });
      toast.success('Location set up successfully!');
      onComplete();
    } catch (error) {
      toast.error('Failed to set up location');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="location-name">Store Name</Label>
        <Input
          id="location-name"
          data-testid="location-name"
          placeholder="Downtown Grocery"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />
      </div>
      <div>
        <Label htmlFor="location-address">Address</Label>
        <Input
          id="location-address"
          data-testid="location-address"
          placeholder="123 Main St, City, State ZIP"
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          required
        />
      </div>
      <div>
        <Label htmlFor="location-charity">Partner Charity</Label>
        <Select value={formData.charity_id} onValueChange={(value) => setFormData({ ...formData, charity_id: value })}>
          <SelectTrigger data-testid="location-charity">
            <SelectValue placeholder="Select charity" />
          </SelectTrigger>
          <SelectContent>
            {charities.map((charity) => (
              <SelectItem key={charity.id} value={charity.id}>
                {charity.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button type="submit" disabled={loading} className="w-full">
        {loading ? 'Setting up...' : 'Set Up Location'}
      </Button>
    </form>
  );
}
